import { abortSignalAfter } from '../../lib/abortSignalAfter';
import { apiGet, apiPost } from '../../lib/api_v2';
import { logError } from '../../lib/logger';

import type {
  ChannelId,
  ClientRequestId,
  FeedApi,
  FeedAroundAnchor,
  FeedPage,
  MessageId,
  MessageUpsertSyncEventType,
  SortOrder,
  SyncEvent,
  SyncResult,
} from '../core/types';
import { EchoFeedApiError, parseApiResponse } from './http';
import type { EchoProfileStore } from './profileStore';
import { sendEchoMessage } from './send';
import {
  type EchoFeedPage,
  type EchoRendererProfile,
  type EchoWireMessage,
  type EchoWireSyncEvent,
  parseEchoFeedPageResponse,
  parseEchoSyncResponse,
} from './wire';

const DEFAULT_SYNC_COUNT = 100;
// A request that hangs (half-open socket after sleep, dead proxy) would otherwise
// pin the session's in-flight freshness cycle; an abort lets its bounded retry run.
const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

/** Error class thrown by echo API adapters; exported here for existing feed callers. */

/** Error categories used by echo API adapters after transport completion. */

/** Minimal v2 transport surface; callers own auth headers and request interception. */
export interface EchoApiTransport {
  get(path: string, extra?: RequestInit): Promise<Response>;
  post(path: string, json?: unknown, extra?: RequestInit): Promise<Response>;
}

/** Sort-order anchor for around fetches when the message id is absent from the local API cache. */
export type EchoAroundAnchor = FeedAroundAnchor;

/** Configures page-sync batch size and test/runtime transport injection. */
export interface EchoFeedApiOptions {
  readonly profileStore?: EchoProfileStore;
  /** Per-request abort deadline for reads; `0` disables it. */
  readonly requestTimeoutMs?: number;
  readonly syncCount?: number;
  readonly transport?: Partial<EchoApiTransport>;
}

/** Fetches and sends echo-backed feed pages, throwing EchoFeedApiError for API/HTTP/parse failures. */
export class EchoFeedApi implements FeedApi {
  private readonly channelId: ChannelId;
  private readonly get: EchoApiTransport['get'];
  private readonly post: EchoApiTransport['post'];
  private readonly profileStore: EchoProfileStore | undefined;
  private readonly requestTimeoutMs: number;
  private readonly syncCount: number;
  private readonly messageIdBySortOrder = new Map<SortOrder, MessageId>();
  private readonly sortOrderByMessageId = new Map<MessageId, SortOrder>();

  constructor(channelId: ChannelId, options: EchoFeedApiOptions = {}) {
    this.channelId = channelId;
    this.get = options.transport?.get ?? apiGet;
    this.post = options.transport?.post ?? apiPost;
    this.profileStore = options.profileStore;
    this.requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    this.syncCount = options.syncCount ?? DEFAULT_SYNC_COUNT;
  }

  latest(limit?: number): Promise<FeedPage> {
    return this.fetchPage({ limit });
  }

  before(cursor: SortOrder, limit?: number): Promise<FeedPage> {
    return this.fetchPage({ before: cursor, limit });
  }

  after(cursor: SortOrder, limit?: number): Promise<FeedPage> {
    return this.fetchPage({ after: cursor, limit });
  }

  around(anchor: MessageId, limit?: number): Promise<FeedPage>;
  around(anchor: EchoAroundAnchor, limit?: number): Promise<FeedPage>;
  around(anchor: MessageId | EchoAroundAnchor, limit?: number): Promise<FeedPage>;
  around(anchor: MessageId | EchoAroundAnchor, limit?: number): Promise<FeedPage> {
    const resolved = this.resolveAroundAnchor(anchor);
    return this.fetchPage({
      aroundId: resolved.id,
      aroundSortOrder: resolved.sortOrder,
      limit,
    });
  }

  async sync(cursor: string): Promise<SyncResult> {
    const params = new URLSearchParams({
      cursor,
      count: String(this.syncCount),
    });
    const response = await this.get(`${this.feedPath()}/sync?${params.toString()}`, this.readRequestInit());
    const data = await parseApiResponse(response, parseEchoSyncResponse);
    await this.persistRendererProfiles(data.rendererProfiles);
    const events = data.events.flatMap((event) => this.mapSyncEvent(event));
    return {
      events,
      overflow: data.overflow,
      nextCursor: data.nextCursor,
    };
  }

  async send(clientRequestId: ClientRequestId, body: string): Promise<MessageId> {
    const message = await sendEchoMessage(this.channelId, this.post, clientRequestId, body);
    this.rememberMessage(message);
    return message.id;
  }

  private async fetchPage(params: {
    readonly before?: SortOrder;
    readonly after?: SortOrder;
    readonly aroundId?: MessageId;
    readonly aroundSortOrder?: SortOrder;
    readonly limit?: number;
  }): Promise<EchoFeedPage> {
    const query = new URLSearchParams();
    if (params.before !== undefined) query.set('before', params.before);
    if (params.after !== undefined) query.set('after', params.after);
    if (params.aroundSortOrder !== undefined) query.set('around_sort_order', params.aroundSortOrder);
    if (params.aroundId !== undefined) query.set('around_id', params.aroundId);
    if (params.limit !== undefined) query.set('limit', String(params.limit));

    const suffix = query.toString();
    const response = await this.get(`${this.feedPath()}${suffix ? `?${suffix}` : ''}`, this.readRequestInit());
    const page = await parseApiResponse(response, (value, path) => parseEchoFeedPageResponse(value, path, this.channelId));
    await this.persistRendererProfiles(page.rendererProfiles);
    if (page.invalidMessages?.length) {
      logError('message-feed', 'Invalid feed messages recovered', {
        channelId: this.channelId,
        issues: page.invalidMessages,
      });
    }
    for (const message of page.messages) {
      this.rememberMessage(message);
    }
    return page;
  }

  private async persistRendererProfiles(profiles: readonly EchoRendererProfile[]): Promise<void> {
    if (!this.profileStore || profiles.length === 0) return;
    try {
      // Feed entities are renderer projections, not complete profile rows. Preserve
      // fields such as created_at that other local stores use for unread state.
      await this.profileStore.mergeRendererProfiles(profiles);
    } catch (error: unknown) {
      // Best-effort: renderer rows are decorated from the response bundle itself,
      // so a failed local write must not fail an otherwise-successful feed page.
      logError('message-feed', 'Failed to persist feed profile bundle', {
        channelId: this.channelId,
        error,
      });
    }
  }

  private resolveAroundAnchor(anchor: MessageId | EchoAroundAnchor): EchoAroundAnchor {
    if (typeof anchor !== 'string') return anchor;

    const sortOrder = this.sortOrderByMessageId.get(anchor);
    if (!sortOrder) {
      throw new EchoFeedApiError('around sortOrder is required when the message id is not cached', {
        kind: 'parse',
        parseError: {
          path: '$.around',
          message: 'expected { id?, sortOrder } for uncached around requests',
        },
      });
    }
    return { id: anchor, sortOrder };
  }

  private mapSyncEvent(event: EchoWireSyncEvent): SyncEvent[] {
    if (event.message) {
      this.rememberMessage(event.message);
      return [
        {
          cursor: event.cursor,
          type: messageEventType(event.action),
          messageId: event.message.id,
          ...(event.message.clientRequestId === undefined ? {} : { clientRequestId: event.message.clientRequestId }),
          message: event.message,
        },
      ];
    }

    const messageId = this.messageIdBySortOrder.get(event.sortOrder);

    return [
      {
        cursor: event.cursor,
        type: 'message.deleted',
        sortOrder: event.sortOrder,
        ...(messageId === undefined ? {} : { messageId }),
        hardDeleted: true,
      },
    ];
  }

  private rememberMessage(message: EchoWireMessage): void {
    this.messageIdBySortOrder.set(message.sortOrder, message.id);
    this.sortOrderByMessageId.set(message.id, message.sortOrder);
  }

  private feedPath(): string {
    return `/channels/${encodeURIComponent(this.channelId)}/feed`;
  }

  private readRequestInit(): RequestInit {
    const signal = abortSignalAfter(this.requestTimeoutMs);
    return signal ? { signal } : {};
  }
}

function messageEventType(action: Exclude<EchoWireSyncEvent['action'], 'destroy'>): MessageUpsertSyncEventType {
  if (action === 'create') return 'message.created';
  return 'message.updated';
}
